import type { ReactElement } from "react"

import { Skeleton } from "@synapse-analytics/synapse-ui"

import { NUM_OF_MODELS_SKELETONS } from "../../constants"
import type { ModelsListProps } from "./Interfaces"
import { ModelListItem } from "./ModelListItem/ModelListItem"

/**
 * Renders a list of models or loading skeletons when models are still being fetched.
 *
 * @param {ModelsListProps} props - The props for the component.
 * @param {Model[] | undefined} props.modelsArr - Array of model objects to be displayed.
 * @param {boolean} props.isModelsLoading - Flag to indicate if the models are still loading.
 * @returns {ReactElement} A list of model items or skeletons.
 */
export const ModelsList = (props: Readonly<ModelsListProps>): ReactElement => {
  const { modelsArr, isModelsFetching, lastItemRef, hasNextPage, isFetchingNextPage } = props

  return (
    <ul>
      {isModelsFetching && !isFetchingNextPage
        ? Array.from({ length: NUM_OF_MODELS_SKELETONS }).map((_, i) => (
            <div key={i} style={{ marginBottom: "0.5rem" }}>
              <Skeleton variant="rectangular" marginBottom={8} height={25} />
              <Skeleton variant="rectangular" marginBottom={2} height={10} width={"50%"} />
            </div>
          ))
        : modelsArr?.map((model, index) => (
            <ModelListItem
              key={model.uuid}
              model={model}
              lastItemRef={hasNextPage && index === modelsArr.length - 1 ? lastItemRef : undefined}
            />
          ))}
      {isFetchingNextPage &&
        hasNextPage &&
        Array.from({ length: 2 }).map((_, i) => (
          <div key={i} style={{ marginBottom: "0.5rem" }}>
            <Skeleton variant="rectangular" marginBottom={8} height={25} />
            <Skeleton variant="rectangular" marginBottom={2} height={10} width={"50%"} />
          </div>
        ))}
    </ul>
  )
}
